import React from "react";
import { AlertTriangle } from "lucide-react";
import { useLocalization } from "../../../hooks/useLocalization";
import { SHIFT_INTERESTS, SHIFTS_PER_WEEK_OPTIONS, YES_NO_UNSURE, WEEKLY_DAYS, WEEKLY_PERIODS } from "../../../constants/interviewScheduling";

export const AvailabilityStep = ({ form, update }) => {
  const { t } = useLocalization();

  const toggleShiftInterest = (value) => {
    const set = new Set(form.shift_interests);
    set.has(value) ? set.delete(value) : set.add(value);
    update({ shift_interests: Array.from(set) });
  };

  const toggleGridCell = (day, period) => {
    const dayState = { ...form.weekly_availability[day] };
    if (period === "unavailable") {
      update({ weekly_availability: { ...form.weekly_availability, [day]: { opening: false, morning: false, afternoon: false, evening: false, closing: false, unavailable: !dayState.unavailable } } });
      return;
    }
    dayState[period] = !dayState[period];
    dayState.unavailable = false;
    update({ weekly_availability: { ...form.weekly_availability, [day]: dayState } });
  };

  const belowMinimum = form.can_work_min_shifts && (form.can_work_min_shifts !== "YES" || ["1", "2", "UNSURE"].includes(form.shifts_per_week));

  return (
    <div data-testid="wizard-step-availability" className="space-y-5">
      <div>
        <label className="text-sm font-medium text-gray-700 mb-2 block">{t("careers.wizard.shiftInterestsLabel")}</label>
        <div className="flex flex-wrap gap-2">
          {SHIFT_INTERESTS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              data-testid={`shift-interest-${opt.value}`}
              onClick={() => toggleShiftInterest(opt.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                form.shift_interests.includes(opt.value) ? "bg-blue-950 text-white border-blue-950" : "bg-white text-gray-600 border-gray-300 hover:border-blue-950"
              }`}
            >
              {t(opt.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-gray-700 mb-2 block">{t("careers.wizard.shiftsPerWeekLabel")}</label>
        <div className="flex flex-wrap gap-2">
          {SHIFTS_PER_WEEK_OPTIONS.map((opt) => (
            <button
              key={opt}
              type="button"
              data-testid={`shifts-per-week-${opt}`}
              onClick={() => update({ shifts_per_week: opt })}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                form.shifts_per_week === opt ? "bg-blue-950 text-white border-blue-950" : "bg-white text-gray-600 border-gray-300 hover:border-blue-950"
              }`}
            >
              {t(`careers.wizard.shiftsPerWeekOption.${opt}`)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-gray-700 mb-2 block">{t("careers.wizard.canWorkMinShiftsLabel")}</label>
        <div className="flex gap-2">
          {YES_NO_UNSURE.map((opt) => (
            <button
              key={opt}
              type="button"
              data-testid={`can-work-min-shifts-${opt}`}
              onClick={() => update({ can_work_min_shifts: opt })}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                form.can_work_min_shifts === opt ? "bg-blue-950 text-white border-blue-950" : "bg-white text-gray-600 border-gray-300 hover:border-blue-950"
              }`}
            >
              {t(`careers.wizard.yesNoUnsure.${opt}`)}
            </button>
          ))}
        </div>
        {belowMinimum && (
          <p data-testid="availability-review-notice" className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {t("careers.wizard.availabilityReviewNotice")}
          </p>
        )}
      </div>

      <div>
        <label className="text-sm font-medium text-gray-700 mb-2 block">{t("careers.wizard.weeklyGridLabel")}</label>
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-[10px] sm:text-xs border-collapse" data-testid="weekly-availability-grid">
            <thead>
              <tr>
                <th className="text-left pb-1 pr-1 text-gray-500 font-medium">{t("careers.wizard.dayColumn")}</th>
                {WEEKLY_PERIODS.map((p) => (
                  <th key={p} className="pb-1 px-1 text-gray-500 font-medium">{t(`careers.wizard.period.${p}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {WEEKLY_DAYS.map((day) => (
                <tr key={day} data-testid={`weekly-grid-row-${day}`}>
                  <td className="py-1 pr-1 font-semibold text-blue-950 whitespace-nowrap">{t(`careers.wizard.day.${day}`)}</td>
                  {WEEKLY_PERIODS.map((period) => (
                    <td key={period} className="text-center px-0.5 py-1">
                      <button
                        type="button"
                        data-testid={`weekly-grid-cell-${day}-${period}`}
                        onClick={() => toggleGridCell(day, period)}
                        className={`w-5 h-5 sm:w-6 sm:h-6 rounded border transition-colors ${
                          form.weekly_availability[day]?.[period]
                            ? period === "unavailable" ? "bg-gray-400 border-gray-400" : "bg-emerald-500 border-emerald-500"
                            : "bg-white border-gray-300"
                        }`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">{t("careers.wizard.earliestStartLabel")}</label>
        <input data-testid="apply-earliest-start-input" type="date" value={form.earliest_start_date} onChange={(e) => update({ earliest_start_date: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
      </div>

      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">{t("careers.wizard.schedulingRestrictionsLabel")}</label>
        <textarea data-testid="apply-scheduling-restrictions-input" rows={2} value={form.scheduling_restrictions} onChange={(e) => update({ scheduling_restrictions: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
      </div>
    </div>
  );
};
